import React, { useRef } from "react"
import { motion, useInView, useReducedMotion } from "framer-motion"
import LandingExpectCard from "./LandingExpectCard"

const WorshipIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3v18M8 8c0-2 1.5-3 4-3s4 1 4 3M6 12c0-2 2-4 6-4s6 2 6 4"
      stroke="#00205c"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const BibleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 4h12a2 2 0 0 1 2 2v14H7a2 2 0 0 0-2 2V4z"
      stroke="#00205c"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M5 20V6a2 2 0 0 1 2-2h12" stroke="#9e7e43" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const CommunityIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="8" r="3" stroke="#00205c" strokeWidth="1.5" />
    <circle cx="16" cy="10" r="2.5" stroke="#00205c" strokeWidth="1.5" />
    <path
      d="M4 19c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5M13 19c0-1.8 1.5-3.2 3.5-3.2"
      stroke="#9e7e43"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const GrowthIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 20V8M12 8c-2 0-3.5-1.5-3.5-3.5M12 8c2 0 3.5-1.5 3.5-3.5"
      stroke="#00205c"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path d="M6 20h12" stroke="#9e7e43" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const expectItems = [
  {
    title: "Worship Experience",
    subtitle: "Spirit-filled worship",
    icon: <WorshipIcon />,
    accent: "bg-gradient-to-br from-[#D6E5FF] to-[#b8d1ff]/60",
  },
  {
    title: "Biblical Teaching",
    subtitle: "Clear Bible teaching",
    icon: <BibleIcon />,
    accent: "bg-gradient-to-br from-[#f0e6d0] to-[#9e7e43]/20",
  },
  {
    title: "Community",
    subtitle: "A welcoming family",
    icon: <CommunityIcon />,
    accent: "bg-gradient-to-br from-[#D6E5FF] to-dc-primary-300/50",
  },
  {
    title: "Spiritual Growth",
    subtitle: "Grow in your faith",
    icon: <GrowthIcon />,
    accent: "bg-gradient-to-br from-[#ebebeb] to-[#D6E5FF]/40",
  },
]

const LandingExpectSection: React.FC = () => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: "-40px" })
  const prefersReducedMotion = useReducedMotion()

  return (
    <section className="relative overflow-hidden bg-[#fafafa] px-4 py-16 md:px-8 md:py-20 lg:px-20">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(214,229,255,0.45),transparent_55%)]" />
      <div className="pointer-events-none absolute -right-24 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-[#9e7e43]/8 blur-3xl" />

      <div ref={ref} className="relative mx-auto flex max-w-5xl flex-col items-center gap-10">
        <header className="flex max-w-2xl flex-col gap-2 text-center">
          <p className="font-dm-sans text-xs font-semibold uppercase tracking-[0.22em] text-[#9e7e43]">
            Your first visit
          </p>
          <h2 className="font-outfit text-3xl font-semibold tracking-tight text-[#0d0d0d] md:text-4xl">
            What to Expect
          </h2>
          <p className="font-dm-sans text-base leading-7 tracking-wide text-[#5a5a5a]">
            A welcoming environment where you can grow in faith, connect with others, and experience God
            in a real way.
          </p>
        </header>

        <div className="grid w-full grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-5">
          {expectItems.map((item, index) => (
            <motion.div
              key={item.title}
              initial={prefersReducedMotion ? undefined : { opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.55,
                delay: prefersReducedMotion ? 0 : index * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <LandingExpectCard {...item} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default LandingExpectSection
